/** What an outline's inputs say beyond their value — the record kind's rules' issues and
 *  the server's errors on them (a 400, a preview conflict) — as a small external store with
 *  per-record subscriptions: an input re-renders when ITS messages change, never because
 *  another row's did. (The record editor's own context re-renders every consumer on every
 *  draft change; the outline's bridge reads it once and publishes here.) */
import { useCallback, useSyncExternalStore } from 'react';

import type { RecordKey } from '@/features/records/draft/changeSet';

/** One rule's finding on one input (a record kind's validation of its outline). */
export interface OutlineIssue {
  rec: RecordKey;
  path: string;
  message: string;
  /** Caused by the draft: Save is blocked until it is fixed. */
  blocking: boolean;
}

export interface PathIssues {
  /** Messages that block Save (the rules' blocking issues, server errors). */
  errors: string[];
  /** Notes on a value stored like that (not caused by the draft; never blocking). */
  notes: string[];
}

export type RecordIssues = Readonly<Record<string, PathIssues>>;

const key = (rec: RecordKey, path: string) => `${rec}|${path}`;

export class OutlineIssues {
  private rules = new Map<string, OutlineIssue[]>();
  private server = new Map<string, string[]>();
  private snapshots = new Map<RecordKey, RecordIssues | undefined>();
  private blockingCache: OutlineIssue[] | null = null;
  private listeners = new Set<() => void>();

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /** The rules' issues (the whole set, replacing the last). */
  setRules(issues: readonly OutlineIssue[]): void {
    const next = new Map<string, OutlineIssue[]>();
    for (const i of issues) next.set(key(i.rec, i.path), [...(next.get(key(i.rec, i.path)) ?? []), i]);
    const changed = this.diff(this.rules, next, (a, b) => JSON.stringify(a) === JSON.stringify(b));
    this.rules = next;
    this.emit(changed);
  }

  /** The server's messages by `rec|path` (the whole set, replacing the last). */
  setServer(messages: ReadonlyMap<string, string[]>): void {
    const next = new Map(messages);
    const changed = this.diff(this.server, next, (a, b) => JSON.stringify(a) === JSON.stringify(b));
    this.server = next;
    this.emit(changed);
  }

  get = (rec: RecordKey): RecordIssues | undefined => {
    if (this.snapshots.has(rec)) return this.snapshots.get(rec);
    const out: Record<string, PathIssues> = {};
    const prefix = `${rec}|`;
    for (const [k, list] of this.rules) {
      if (!k.startsWith(prefix)) continue;
      const path = k.slice(prefix.length);
      const p = (out[path] ??= { errors: [], notes: [] });
      for (const i of list) (i.blocking ? p.errors : p.notes).push(i.message);
    }
    for (const [k, list] of this.server) {
      if (!k.startsWith(prefix)) continue;
      const path = k.slice(prefix.length);
      const p = (out[path] ??= { errors: [], notes: [] });
      for (const m of list) if (!p.errors.includes(m)) p.errors.push(m);
    }
    const snapshot = Object.keys(out).length > 0 ? out : undefined;
    this.snapshots.set(rec, snapshot);
    return snapshot;
  };

  /** Every blocking rule issue (for a summary line) — a stable snapshot. */
  blocking = (): OutlineIssue[] => {
    this.blockingCache ??= [...this.rules.values()].flat().filter((i) => i.blocking);
    return this.blockingCache;
  };

  private diff<T>(a: Map<string, T>, b: Map<string, T>, eq: (x: T, y: T) => boolean): Set<RecordKey> {
    const recs = new Set<RecordKey>();
    for (const k of new Set([...a.keys(), ...b.keys()])) {
      const x = a.get(k);
      const y = b.get(k);
      if (x === undefined || y === undefined ? x !== y : !eq(x, y)) recs.add(k.slice(0, k.lastIndexOf('|')));
    }
    return recs;
  }

  private emit(recs: Set<RecordKey>): void {
    if (recs.size === 0) return;
    this.blockingCache = null;
    for (const rec of recs) this.snapshots.delete(rec);
    for (const l of [...this.listeners]) l();
  }
}

/** The blocking rule issues of the whole outline. */
export function useBlockingIssues(store: OutlineIssues): OutlineIssue[] {
  return useSyncExternalStore(store.subscribe, store.blocking);
}

/** The messages on one record's inputs (undefined: none). */
export function useRecordIssues(store: OutlineIssues, rec: RecordKey): RecordIssues | undefined {
  const getSnapshot = useCallback(() => store.get(rec), [store, rec]);
  return useSyncExternalStore(store.subscribe, getSnapshot);
}
